import React, { useState, useRef, useEffect, useCallback } from 'react';
// Assuming apiCall and its path are correct for your project structure
import apiCall from '../helper/apiCall';
// *** NEW: Assuming usePrint.js is in the '../hooks/' directory ***
import usePrint from './usePrint';
import ShippingLabel from './ShippingLabel';
// *** IMPORTANT: If you use a real barcode, install it, e.g., import Barcode from 'react-barcode'; ***


// =========================================================================================
// 2. MAIN COMPONENT: OfflineOrderEntry
// =========================================================================================

const OfflineOrderEntry = () => {
    // --- State Management ---
    const COD_AMOUNT = 100;
    const [orderItems, setOrderItems] = useState([]);
    const [currentBarcode, setCurrentBarcode] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Master Data State
    const [statesList, setStatesList] = useState([]);
    const [countriesList, setCountriesList] = useState([]);
    const [couponsList, setCouponsList] = useState([]);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [manualDiscount, setManualDiscount] = useState(0);
    const [codCharge, setCodCharge] = useState(0);
    const [paymentMethodList, setPaymentMethodList] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    // Customer Data State
    const [customerMobile, setCustomerMobile] = useState('');

    const [selectedImages, setSelectedImages] = useState(null);

    const handleOpenGallery = (images) => {
            setSelectedImages(images);
        };
    const [customerData, setCustomerData] = useState({
        customer_id: null,
        customer_name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state_id: '',
        country_id: '',
        land_mark: '',
        pincode: '',
    });
    const [addressLookupLoading, setAddressLookupLoading] = useState(false);

    // NEW STATE: Holds confirmed order details for label printing
    const [lastOrder, setLastOrder] = useState(null);

    const barcodeInputRef = useRef(null);
    // 🚨 MODIFIED REF: Reference to the component to be printed (for usePrint hook)
    const labelRef = useRef(null);
    const [isShipmentRequired, setIsShipmentRequired] = useState(false)

    // --- Lifecycle and Master Data Fetch ---

    // Fetch States and Countries on component mount
    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const statesRes = await apiCall("getStates");
                const countriesRes = await apiCall("getCountries");
                const couponsRes = await apiCall("getCoupons");
                const paymentMethodRes = await apiCall("getRetailPaymentMethods");
                if (statesRes.success && statesRes.data) {
                    setStatesList(statesRes.data);
                } else {
                    console.error("Failed to fetch states:", statesRes.message);
                }

                if (countriesRes.success && countriesRes.data) {
                    setCountriesList(countriesRes.data);
                } else {
                    console.error("Failed to fetch countries:", countriesRes.message);
                }
                if (couponsRes.success) {
                    setCouponsList(couponsRes.data);
                } else {
                    console.error("Failed to fetch coupons:", couponsRes.message);
                }
                if (paymentMethodRes.success && paymentMethodRes.data) {
                    setPaymentMethodList(paymentMethodRes.data);
                } else {
                    console.error("Failed to fetch payment method:", paymentMethodRes.message);
                }

            } catch (error) {
                console.error("Error fetching master data:", error);
                setMessage("System Error: Failed to load state/country lists.");
            }
        };
        fetchMasters();
    }, []);

    // Auto-focus the barcode input on load/state change
    useEffect(() => {
        if (barcodeInputRef.current) {
            // Focus only if we are not busy and no order is already created
            if (!loading && !isSubmitting && !lastOrder) {
                barcodeInputRef.current.focus();
            }
        }
    }, [loading, isSubmitting, lastOrder]);

    // --- Calculations ---
    const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * parseFloat(item.product_item_sell_price || 0)), 0);

    const calculateCODCharges = () => {
        if (!selectedPaymentMethod) return 0.00;
        // If Retail coupon (manager override), use manual input
        if (selectedPaymentMethod.payment_method_id == 1 ) {
            return parseFloat(COD_AMOUNT || 0);
        }      
        return parseFloat( 0);
    };

    const calculateDiscount = () => {
        if (!selectedCoupon) return 0;
        // If Retail coupon (manager override), use manual input
        if (selectedCoupon.channel === 'Retail' || selectedCoupon.discount_value === 0) {
            return parseFloat(manualDiscount || 0);
        }
        // Standard logic for fixed/percentage
        if (selectedCoupon.discount_type === 'PERCENTAGE') {
            return (subtotal * selectedCoupon.discount_value) / 100;
        }
        return parseFloat(selectedCoupon.discount_value || 0);
    };

    const codChargeAmt = calculateCODCharges();

    const discountAmount = calculateDiscount();

    const finalTotal = Math.max(0, subtotal - discountAmount + codChargeAmt);

    // --- Address/Customer Logic ---

    const handleAddressChange = (field, value) => {
        setCustomerData(prev => ({ ...prev, [field]: value }));
    };

    const handleMobileChange = (e) => {
        const value = e.target.value;
        setCustomerMobile(value);
        if (value.length !== 10) {
            // Clear customer details if mobile number is incomplete
            setCustomerData({
                customer_id: null,
                customer_name: '',
                address_line_1: '',
                address_line_2: '',
                city: '',
                state_id: '',
                country_id: '1',
                land_mark: '',
                pincode: '',
            });
        }
    };

    // Function to look up the customer address
    const lookupCustomerAddress = useCallback(async () => {
        if (customerMobile.length === 10) {
            setAddressLookupLoading(true);
            setMessage(`Searching for customer ${customerMobile}...`);

            try {
                const response = await apiCall("getOfflineCustomerDetails", { mobile_no: customerMobile });

                if (response.success && response.data && response.data.customer) {
                    const customer = response.data.customer;
                    const addresses = response.data.addresses;
                    const primaryAddress = addresses && addresses.length > 0 ? addresses[0] : {};

                    setCustomerData({
                        customer_id: customer.customer_id,
                        customer_name: customer.customer_name,
                        address_line_1: primaryAddress.address_line_1 || '',
                        address_line_2: primaryAddress.address_line_2 || '',
                        city: primaryAddress.city || '',
                        pincode: primaryAddress.pincode || '',
                        state_id: primaryAddress.state_id || '',
                        country_id: primaryAddress.country_id || '',
                        land_mark: primaryAddress.land_mark || '',
                    });

                    setMessage(`Customer found: ${customer.customer_name}. Address loaded.`);
                } else {
                    setMessage("New customer or no address found. Please fill in the shipping details.");
                    setCustomerData(prev => ({
                        ...prev,
                        customer_id: null,
                        customer_name: ''
                    }));
                }
            } catch (error) {
                setMessage(`Error during customer lookup: ${error.message}`);
                setCustomerData(prev => ({ ...prev, customer_id: null, customer_name: '' }));
            } finally {
                setAddressLookupLoading(false);
            }
        }
    }, [customerMobile]);

    // Trigger lookup when mobile number is complete
    useEffect(() => {
        if (customerMobile.length === 10) {
            lookupCustomerAddress();
        }
    }, [customerMobile, lookupCustomerAddress]);

    // --- Order Item Logic ---

    const handleBarcodeSubmit = async (e) => {
        e.preventDefault();
        const barcode = currentBarcode.trim();
        setCurrentBarcode('');

        if (!barcode || loading || isSubmitting || lastOrder) return; // Block if order is done

        setLoading(true);
        setMessage(`Searching for barcode: ${barcode}...`);

        try {
            const response = await apiCall("getProductDetailsByBarcode", { barcode_number: barcode });

            if (response.success && response.data) {
                const variantData = response.data;
                const scannedItemId = variantData.product_item_id;

                const existingItemIndex = orderItems.findIndex(item => item.product_item_id === scannedItemId);

                if (existingItemIndex !== -1) {
                    setOrderItems(prevItems => prevItems.map((item, index) =>
                        index === existingItemIndex && item.quantity < item.product_item_stock_qty
                            ? { ...item, quantity: item.quantity + 1 }
                            : item
                    ));
                    setMessage(`Increased quantity of ${scannedItemId}.`);
                } else {
                    // Check stock before adding
                    if (variantData.product_item_stock_qty >= 1) {
                        setOrderItems(prevItems => [
                            ...prevItems,
                            { ...variantData, quantity: 1 }
                        ]);
                        setMessage(`Added new item ${scannedItemId} (${variantData.category_name}).`);
                    } else {
                        setMessage(`Error: Item ${scannedItemId} is out of stock.`);
                    }
                }
            } else {
                setMessage(`Error: Barcode ${barcode} not found.`);
            }
        } catch (error) {
            setMessage(`Error fetching product: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = (itemId) => {
        setOrderItems(prevItems => prevItems.filter(item => item.product_item_id !== itemId));
        setMessage(`Item deleted from the order.`);
    };

    const handleQuantityChange = (itemId, newQty) => {
        const qty = Math.max(1, parseInt(newQty) || 1);

        setOrderItems(prevItems => prevItems.map(item =>
            item.product_item_id === itemId
                ? { ...item, quantity: Math.min(qty, item.product_item_stock_qty) }
                : item
        ));
    };

    // --- Order Submission ---

    const handleCreateOrder = async () => {
        // Validation check for mandatory address fields
        const isAddressComplete = customerData.customer_name && customerData.address_line_1 && customerData.city && customerData.state_id &&  customerData.pincode && customerMobile.length === 10;
        console.log('customerData:',customerData);
        if (!isAddressComplete) {
            setMessage("Error: Please complete customer mobile number and shipping address fields.");
            return;
        }

        if (!selectedPaymentMethod.payment_method_id) {
            setMessage("Error: Please select payment method.");
            return;
        }

        if (orderItems.length === 0) {
            setMessage("Error: Scan products first.");
            return;
        }

        // Reset previous order details before starting new submission
        setLastOrder(null);
        setIsSubmitting(true);
        setMessage("Creating order...");

        const orderData = {
            customer_mobile: customerMobile,
            customer_id: customerData.customer_id,
            customer_name: customerData.customer_name,
            shipping_details: customerData,
            is_shipment_required : isShipmentRequired,
            items: orderItems.map(item => ({
                product_id: item.product_id,
                product_item_id: item.product_item_id,
                quantity: item.quantity,
                // Ensure price and mrp are sent as numbers/strings, not float objects
                price: parseFloat(item.product_item_sell_price),
                mrp_at_purchase: parseFloat(item.product_item_mrp),
            })),
            coupon_code: selectedCoupon?.coupon_code || null,
            discount_amount: discountAmount,
            payment_method_id: selectedPaymentMethod.payment_method_id,
            cod_charges_by_customer: codCharge,
            total_amount: finalTotal,
            //total_amount: orderItems.reduce((sum, item) => sum + (item.quantity * parseFloat(item.product_item_sell_price || 0)), 0),
        };

        try {
            const response = await apiCall("createOfflineOrder", orderData);

            if (response.success) {
                const { order_number, order_id, shipment_id } = response.data;
                setMessage(`Order created successfully! Order No: ${order_number}. Printing label is now enabled.`);

                // Store necessary data for printing
                setLastOrder(response.data);

            } else {
                setMessage(`Order creation failed: ${response.message || 'Unknown error'}`);
            }
        } catch (error) {
            setMessage(`Order submission failed: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setOrderItems([]);
        setCurrentBarcode('');
        setCustomerMobile('');
        setCustomerData({
            customer_id: null,
            customer_name: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state_id: '',
            country_id: '',
            land_mark: '',
            pincode: '',
        });
        setLastOrder(null); // Clear the last order details
        setManualDiscount(null);
        setCodCharge(0);
        setSelectedPaymentMethod(null);
        setMessage("Ready for new order.");
    }

    //const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * parseFloat(item.product_item_sell_price || 0)), 0);
    const inputClass = "p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full";


    // 🚨 MODIFIED: Use the custom hook with the labelRef
    const handlePrint = usePrint(labelRef, `Shipping Label - ${lastOrder?.order_number || 'Order'}`);

    return (
//         <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg">

//             {/* --- HEADER: Offline Order & Scanner Input --- */}
//             <header className="mb-6 pb-4 border-b border-gray-200">
//                 <h2 className="text-3xl font-extrabold text-blue-800 mb-4">Offline Order Entry</h2>

//                 <form onSubmit={handleBarcodeSubmit} className="flex gap-4 items-center">
//                     <label htmlFor="barcode-scanner" className="text-lg font-semibold text-gray-700 w-32">
//                         Scan Product:
//                     </label>
//                     <input
//                         ref={barcodeInputRef}
//                         id="barcode-scanner"
//                         type="text"
//                         value={currentBarcode}
//                         onChange={(e) => setCurrentBarcode(e.target.value)}
//                         placeholder="Barcode Input..."
//                         className="flex-grow p-3 border border-blue-400 rounded-lg shadow-inner focus:ring-blue-500 focus:border-blue-500 text-lg"
//                         disabled={loading || isSubmitting || !!lastOrder} // Disable input after order creation
//                     />
//                     <button
//                         type="submit"
//                         className={`px-6 py-3 rounded-lg text-white font-semibold transition-colors ${loading || isSubmitting || !!lastOrder ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
//                             }`}
//                         disabled={loading || isSubmitting || !!lastOrder} // Disable button after order creation
//                     >
//                         {loading ? 'Processing...' : 'Add Item'}
//                     </button>
//                 </form>
//             </header>

//             {/* Status Message */}
//             <div className={`p-3 mb-6 rounded-lg font-medium ${message.startsWith('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
//                 {message || "Scan a product barcode to begin the order."}
//             </div>

//             {/* --- 1. ORDER ITEMS TABLE --- */}
//             {orderItems.length > 0 ? (
//                 <div className="overflow-x-auto mb-8 border rounded-lg shadow-sm">
//                     <table className="min-w-full divide-y divide-gray-200">
//                         <thead className="bg-gray-50">
//                             <tr>
//                                 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
//                                 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product & Category</th>
//                                 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Purchase Price</th>
//                                 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available Qty</th>
//                                 <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Booked Qty</th>
//                                 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Line Total (Sell)</th>
//                                 <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
//                             </tr>
//                         </thead>
//                         <tbody className="bg-white divide-y divide-gray-200">
//                             {orderItems.map(item => (
//                                 <tr key={item.product_item_id} className="hover:bg-yellow-50/50">
//                                     {/* <td className="px-4 py-2 whitespace-nowrap">
//                                         {item.product_item_images && item.product_item_images.length > 0 && (
//                                             <img src={item.product_item_images[0]} alt={item.product_item_id} className="h-10 w-10 rounded-md object-cover" />
//                                         )}
//                                     </td> */}
//                                     <>
//     <td className="px-4 py-2 whitespace-nowrap">
//       {item.product_item_images?.length > 0 && (
//         <img
//           // Accessing the 'thumbnail' property of the first object
//           src={item.product_item_images[0].thumbnail} 
//           alt={item.product_item_id}
//           className="h-10 w-10 rounded-md object-cover cursor-pointer hover:opacity-80 transition"
//           onClick={() => handleOpenGallery(item.product_item_images)}
//         />
//       )}
//     </td>

//     {/* Simple Modal Overlay to show all 'main' images */}
//     {selectedImages && (
//       <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
//         <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
//           <div className="flex justify-between items-center mb-4">
//             <h3 className="text-lg font-bold">Product Images</h3>
//             <button 
//               onClick={() => setSelectedImages(null)}
//               className="text-red-500 font-bold text-xl"
//             >✕</button>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             {selectedImages.map((img, index) => (
//               <img 
//                 key={index} 
//                 src={img.main} 
//                 alt="Product" 
//                 className="w-full h-auto rounded shadow-sm" 
//               />
//             ))}
//           </div>
//         </div>
//       </div>
//     )}
//   </>
//                                     <td className="px-4 py-2">
//                                         <div className="text-sm font-medium text-gray-900">{item.product_name} ({item.product_item_id})</div>
//                                         <div className="text-xs text-gray-500">
//                                             {item.manufacturer_name ? `${item.manufacturer_name} | ` : ''}
//                                             <span className="font-semibold text-indigo-600">{item.category_name}</span>
//                                         </div>
//                                         <div className="text-xs text-gray-500">
//                                             {item.size_name && `Size: ${item.size_name}`}
//                                             {item.color_name && ` | Color: ${item.color_name}`}
//                                         </div>
//                                     </td>
//                                     <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-right">
//                                         ₹{parseFloat(item.purchase_price || 0).toFixed(2)}
//                                     </td>
//                                     <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-right">
//                                         {item.product_item_stock_qty || 0}
//                                     </td>
//                                     <td className="px-4 py-2 text-center">
//                                         <input
//                                             type="number"
//                                             min="1"
//                                             max={item.product_item_stock_qty}
//                                             value={item.quantity}
//                                             onChange={(e) => handleQuantityChange(item.product_item_id, e.target.value)}
//                                             className="w-16 p-1 border rounded text-center focus:ring-blue-500"
//                                             disabled={isSubmitting || !!lastOrder}
//                                         />
//                                     </td>
//                                     <td className="px-4 py-2 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
//                                         ₹{(item.quantity * parseFloat(item.product_item_sell_price || 0)).toFixed(2)}
//                                     </td>
//                                     <td className="px-4 py-2 text-center">
//                                         <button
//                                             onClick={() => handleDeleteItem(item.product_item_id)}
//                                             className="text-red-600 hover:text-red-900 transition-colors"
//                                             disabled={isSubmitting || !!lastOrder}
//                                         >
//                                             Delete
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                         <tfoot>
                            
//                             {/* <tr>
//                                 <td colSpan="5" className="px-4 py-3 text-right text-base font-bold text-gray-900 bg-gray-100">ORDER SUB TOTAL</td>
//                                 <td className="px-4 py-3 text-right text-base font-bold text-blue-600 bg-gray-100">₹{subtotal.toFixed(2)}</td>
//                                 <td className="bg-gray-100"></td>
//                             </tr> */}
//                         </tfoot>
                        
//                     </table>
//                     <div className="grid grid-cols-2 gap-6 mb-8">
//                                 <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
//                                     <h3 className="font-bold mb-2">Payment method</h3>
//                                     <select
//                                         className={inputClass}
//                                         value={selectedPaymentMethod?.payment_method_id || ''}
//                                         onChange={(e) => {
//                                             const c = paymentMethodList.find(x => x.payment_method_id == e.target.value);
//                                             setSelectedPaymentMethod(c || null);
//                                             setCodCharge(0);
                                            
//                                         }}
//                                     >
//                                         <option value="">Select payment</option>
//                                         {paymentMethodList.map(c => <option key={c.payment_method_id} value={c.payment_method_id}>{c.payment_method_name}</option>)}
//                                     </select>
//                                     {selectedPaymentMethod?.payment_method_id == 1 && (
//                                         <div className="mt-2">
//                                             <label className="text-xs font-bold text-red-600">ENTER COD VALUE (₹)</label>
//                                             <input type="number" className={inputClass} value={codCharge} onChange={(e) => setCodCharge(e.target.value)} />
//                                         </div>
//                                     )}
//                                 </div>
//                                 <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
//                                     <h3 className="font-bold mb-2">Apply Discount</h3>
//                                     <select
//                                         className={inputClass}
//                                         value={selectedCoupon?.coupon_code || ''}
//                                         onChange={(e) => {
//                                             const c = couponsList.find(x => x.coupon_code === e.target.value);
//                                             setSelectedCoupon(c || null);
//                                             setManualDiscount(0);
//                                         }}
//                                     >
//                                         <option value="">Select Coupon</option>
//                                         {couponsList.map(c => <option key={c.coupon_code} value={c.coupon_code}>{c.coupon_code}</option>)}
//                                     </select>
//                                     {selectedCoupon?.channel === 'Retail' && (
//                                         <div className="mt-2">
//                                             <label className="text-xs font-bold text-red-600">ENTER DISCOUNT VALUE (₹)</label>
//                                             <input type="number" className={inputClass} value={manualDiscount} onChange={(e) => setManualDiscount(e.target.value)} />
//                                         </div>
//                                     )}
//                                 </div>
//                                 <div className="p-4 bg-gray-50 rounded-lg border">
//                                     <div className="flex justify-between"><span>Subtotal:</span><span>₹{subtotal.toFixed(2)}</span></div>
//                                     <div className="flex justify-between text-green-600"><span>Discount:</span><span>- ₹{discountAmount.toFixed(2)}</span></div>
//                                     {selectedPaymentMethod?.payment_method_id === 1 && (
//                                         <div className="flex justify-between text-sm text-gray-600">
//                                             <span>COD charges:</span>
//                                             <span className="font-medium">₹{codCharge}</span>
//                                         </div>
//                                     )}
//                                     <div className="flex justify-between font-bold text-lg mt-2 border-t pt-2 text-blue-800">
//                                         <span>Grand Total:</span><span>₹{finalTotal.toFixed(2)}</span>
//                                     </div>
//                                 </div>
//                             </div>
//                 </div>
//             ) : (
//                 <p className="text-gray-500 italic mb-8 text-center">Start scanning products to populate the order.</p>
//             )}

//             {/* --- 2. ADDRESS SECTION --- */}
//             <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-gray-50">
//                 <h3 className="text-xl font-bold mb-4 text-gray-700 flex items-center">
//                     Customer & Shipping Details
//                     {addressLookupLoading && (
//                         <span className="ml-3 text-sm text-blue-500">Searching...</span>
//                     )}
//                 </h3>

//                 <div className="grid grid-cols-2 gap-4">
//                     {/* Mobile Number Lookup */}
//                     <div className="col-span-2 sm:col-span-1">
//                         <label className="block text-sm font-medium text-gray-700">Mobile Number (10 digits)</label>
//                         <input
//                             type="text"
//                             maxLength="10"
//                             value={customerMobile}
//                             onChange={handleMobileChange}
//                             className={inputClass}
//                             placeholder="e.g., 9876543210"
//                             disabled={isSubmitting || !!lastOrder}
//                         />
//                     </div>

//                     {/* Customer Name */}
//                     <div className="col-span-2 sm:col-span-1">
//                         <label className="block text-sm font-medium text-gray-700">Customer Name</label>
//                         <input
//                             type="text"
//                             value={customerData.customer_name}
//                             onChange={(e) => handleAddressChange('customer_name', e.target.value)}
//                             className={inputClass}
//                             placeholder="Required"
//                             disabled={isSubmitting || !!lastOrder || customerData.customer_id}
//                         />
//                     </div>

//                     {/* Address Line 1 */}
//                     <div className="col-span-2">
//                         <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
//                         <input
//                             type="text"
//                             value={customerData.address_line_1}
//                             onChange={(e) => handleAddressChange('address_line_1', e.target.value)}
//                             className={inputClass}
//                             placeholder="Street address, Flat/House No."
//                             disabled={isSubmitting || !!lastOrder}
//                         />
//                     </div>

//                     {/* Address Line 2 */}
//                     <div className="col-span-2">
//                         <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
//                         <input
//                             type="text"
//                             value={customerData.address_line_2}
//                             onChange={(e) => handleAddressChange('address_line_2', e.target.value)}
//                             className={inputClass}
//                             placeholder="Area, Colony Name"
//                             disabled={isSubmitting || !!lastOrder}
//                         />
//                     </div>

//                     {/* City, Pincode, Landmark Grid */}
//                     <div className="col-span-2 grid grid-cols-3 gap-4">
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">City</label>
//                             <input
//                                 type="text"
//                                 value={customerData.city}
//                                 onChange={(e) => handleAddressChange('city', e.target.value)}
//                                 className={inputClass}
//                                 placeholder="City"
//                                 disabled={isSubmitting || !!lastOrder}
//                             />
//                         </div>
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">Pincode</label>
//                             <input
//                                 type="text"
//                                 maxLength="6"
//                                 value={customerData.pincode}
//                                 onChange={(e) => handleAddressChange('pincode', e.target.value)}
//                                 className={inputClass}
//                                 placeholder="Pincode"
//                                 disabled={isSubmitting || !!lastOrder}
//                             />
//                         </div>
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">Landmark (Optional)</label>
//                             <input
//                                 type="text"
//                                 value={customerData.land_mark}
//                                 onChange={(e) => handleAddressChange('land_mark', e.target.value)}
//                                 className={inputClass}
//                                 placeholder="e.g., Near City Hall"
//                                 disabled={isSubmitting || !!lastOrder}
//                             />
//                         </div>
//                     </div>

//                     {/* State and Country Dropdowns (Using IDs) */}
//                     <div className="col-span-2 grid grid-cols-2 gap-4">
//                         {/* State Dropdown */}
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">State</label>
//                             {statesList.length > 0 ? (
//                                 <select
//                                     value={customerData.state_id}
//                                     onChange={(e) => handleAddressChange('state_id', e.target.value)}
//                                     className={inputClass}
//                                     disabled={isSubmitting || !!lastOrder}
//                                 >
//                                     <option value="">Select State</option>
//                                     {statesList.map(s => (
//                                         <option key={s.state_id} value={s.state_id}>
//                                             {s.state_name}
//                                         </option>
//                                     ))}
//                                 </select>
//                             ) : (
//                                 <input
//                                     type="text"
//                                     value={customerData.state_id}
//                                     onChange={(e) => handleAddressChange('state_id', e.target.value)}
//                                     className={inputClass}
//                                     placeholder="State ID"
//                                     disabled={isSubmitting || !!lastOrder}
//                                 />
//                             )}
//                         </div>

//                         {/* Country Dropdown */}
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">Country</label>
//                             {countriesList.length > 0 ? (
//                                 <select
//                                     value={customerData.country_id}
//                                     onChange={(e) => handleAddressChange('country_id', e.target.value)}
//                                     className={inputClass}
//                                     disabled={isSubmitting || !!lastOrder}
//                                 >
//                                     <option value="">Select Country</option>
//                                     {countriesList.map(c => (
//                                         <option key={c.country_id} value={c.country_id}>
//                                             {c.country_name}
//                                         </option>
//                                     ))}
//                                 </select>
//                             ) : (
//                                 <input
//                                     type="text"
//                                     value={customerData.country_id}
//                                     onChange={(e) => handleAddressChange('country_id', e.target.value)}
//                                     className={inputClass}
//                                     placeholder="Country ID"
//                                     disabled={isSubmitting || !!lastOrder}
//                                 />
//                             )}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             {/* --- END ADDRESS SECTION --- */}

//             {/* Action Buttons (Order Creation and Shipping Label) */}
//             <div className="flex justify-between pt-4">
//                 <button
//                     onClick={handleReset}
//                     className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
//                     disabled={isSubmitting}
//                 >
//                     {lastOrder ? 'Start New Order' : 'Clear All'}
//                 </button>

//                 {/* Conditional Rendering for Order/Print Buttons */}
//                 {lastOrder ? (
//                     // Print Button (Enabled after order success)
//                     <button
//                         onClick={handlePrint}
//                         className="px-8 py-3 rounded-lg text-white font-bold bg-purple-600 hover:bg-purple-700 transition-colors"
//                     >
//                         Generate Shipping Label: {lastOrder.orderDetail.order_number}
//                     </button>
//                 ) : (
//                     // Create Order Button (Enabled before order success)
//                     <button
//                         onClick={handleCreateOrder}
//                         className={`px-8 py-3 rounded-lg text-white font-bold transition-colors ${orderItems.length === 0 || isSubmitting
//                             ? 'bg-green-400 cursor-not-allowed'
//                             : 'bg-green-600 hover:bg-green-700'
//                             }`}
//                         disabled={orderItems.length === 0 || isSubmitting}
//                     >
//                         {isSubmitting ? 'Creating Order...' : 'Finalize & Create Order'}
//                     </button>
//                 )}
//             </div>

//             {/* 🚨 CRITICAL ADDITION: The component to be printed (hidden from view) */}
//             {lastOrder && (
//                 <div style={{ display: 'none' }}>
//                     <ShippingLabel
//                         ref={labelRef}
//                         orderDetail={lastOrder.orderDetail}
//                         shippingDetail={lastOrder.shippingDetail}
//                     />
//                 </div>
//             )}

//         </div>
<div className="max-w-6xl mx-auto p-4 md:p-8 bg-gray-50 min-h-screen">
  <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
    
    {/* --- HEADER: Modern Scanner Layout --- */}
    <header className="p-6 border-b border-gray-100 bg-white">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Offline Order Entry</h2>
          <p className="text-sm text-gray-500">Scan items to build a new retail order</p>
        </div>
        
        <form onSubmit={handleBarcodeSubmit} className="flex flex-1 max-w-md gap-2">
          <div className="relative flex-grow">
            <input
              ref={barcodeInputRef}
              id="barcode-scanner"
              type="text"
              value={currentBarcode}
              onChange={(e) => setCurrentBarcode(e.target.value)}
              placeholder="Scan Barcode..."
              className="w-full pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
              disabled={loading || isSubmitting || !!lastOrder}
            />
            <div className="absolute right-3 top-3 text-gray-400">
               <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <button
            type="submit"
            className={`px-6 py-3 rounded-xl font-bold text-white transition-all transform active:scale-95 ${
              loading || isSubmitting || !!lastOrder ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
            }`}
            disabled={loading || isSubmitting || !!lastOrder}
          >
            {loading ? '...' : 'Add'}
          </button>
        </form>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm font-medium animate-pulse ${
          message.startsWith('Error') ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'
        }`}>
          <span>{message}</span>
        </div>
      )}
    </header>

    <div className="p-6">
      {/* --- 1. ORDER ITEMS SECTION --- */}
      {orderItems.length > 0 ? (
        <div className="space-y-6">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-hidden border border-gray-100 rounded-xl shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {orderItems.map(item => (
                  <tr key={item.product_item_id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {item.product_item_images?.length > 0 && (
                          <img
                            src={item.product_item_images[0].thumbnail}
                            alt={item.product_item_id}
                            className="h-12 w-12 rounded-lg object-cover ring-1 ring-gray-200 cursor-zoom-in"
                            onClick={() => handleOpenGallery(item.product_item_images)}
                          />
                        )}
                        <div>
                          <div className="text-sm font-bold text-gray-900">{item.product_name}</div>
                          <div className="text-xs text-blue-600 font-medium">{item.category_name}</div>
                          <div className="text-xs text-gray-400">ID: {item.product_item_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600 whitespace-nowrap">
                      ₹{parseFloat(item.purchase_price || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <input
                          type="number"
                          min="1"
                          max={item.product_item_stock_qty}
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.product_item_id, e.target.value)}
                          className="w-16 p-1.5 border border-gray-200 rounded-md text-center focus:ring-2 focus:ring-blue-500 outline-none"
                          disabled={isSubmitting || !!lastOrder}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-gray-900 whitespace-nowrap">
                      ₹{(item.quantity * parseFloat(item.product_item_sell_price || 0)).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleDeleteItem(item.product_item_id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                        disabled={isSubmitting || !!lastOrder}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {orderItems.map(item => (
              <div key={item.product_item_id} className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm">
                <div className="flex gap-4">
                   {item.product_item_images?.length > 0 && (
                      <img
                        src={item.product_item_images[0].thumbnail}
                        className="h-16 w-16 rounded-lg object-cover"
                        alt="thumb"
                      />
                    )}
                    <div className="flex-1">
                       <h4 className="font-bold text-gray-900 leading-tight">{item.product_name}</h4>
                       <p className="text-xs text-gray-500 mt-1">Stock: {item.product_item_stock_qty}</p>
                       <div className="flex items-center justify-between mt-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(item.product_item_id, e.target.value)}
                            className="w-14 p-1 border rounded text-center"
                          />
                          <span className="font-bold text-blue-700">₹{(item.quantity * item.product_item_sell_price).toFixed(2)}</span>
                          <button onClick={() => handleDeleteItem(item.product_item_id)} className="text-red-500 text-sm font-semibold">Remove</button>
                       </div>
                    </div>
                </div>
              </div>
            ))}
          </div>

          {/* --- PAYMENT & DISCOUNT SECTION --- */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="p-5 border border-emerald-100 rounded-2xl bg-emerald-50/50">
                    <h3 className="text-sm font-bold text-emerald-800 mb-3 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 011 1v2.5a.5.5 0 01-1 0V16zm-7 0a1 1 0 11-2 0 1 1 0 012 0zm9 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                      Shipment Required
                    </h3>
                    <select
                      className={`${inputClass} !bg-white`}
                      value={isShipmentRequired ? 'yes' : 'no'}
                      onChange={(e) => setIsShipmentRequired(e.target.value === 'yes')}
                    >
                      <option value="no">No (In-Store Pickup)</option>
                      <option value="yes">Yes (Home Delivery)</option>
                    </select>
                  </div>
            <div className="p-5 border border-blue-100 rounded-2xl bg-blue-50/50">
              <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Payment Method
              </h3>
              <select
                className={`${inputClass} !bg-white`}
                value={selectedPaymentMethod?.payment_method_id || ''}
                onChange={(e) => {
                  const c = paymentMethodList.find(x => x.payment_method_id == e.target.value);
                  setSelectedPaymentMethod(c || null);
                  setCodCharge(0);
                }}
              >
                <option value="">Select payment</option>
                {paymentMethodList.map(c => <option key={c.payment_method_id} value={c.payment_method_id}>{c.payment_method_name}</option>)}
              </select>
              {selectedPaymentMethod?.payment_method_id == 1 && (
                <div className="mt-3 animate-slide-down">
                  <label className="text-[10px] font-black text-red-500 uppercase tracking-wider">COD Surcharge (₹)</label>
                  <input type="number" className={`${inputClass} !bg-white mt-1`} value={codCharge} onChange={(e) => setCodCharge(e.target.value)} />
                </div>
              )}
            </div>

            <div className="p-5 border border-purple-100 rounded-2xl bg-purple-50/50">
              <h3 className="text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12z" />
                </svg>
                Apply Discount
              </h3>
              <select
                className={`${inputClass} !bg-white`}
                value={selectedCoupon?.coupon_code || ''}
                onChange={(e) => {
                  const c = couponsList.find(x => x.coupon_code === e.target.value);
                  setSelectedCoupon(c || null);
                  setManualDiscount(0);
                }}
              >
                <option value="">Select Coupon</option>
                {couponsList.map(c => <option key={c.coupon_code} value={c.coupon_code}>{c.coupon_code}</option>)}
              </select>
              {selectedCoupon?.channel === 'Retail' && (
                <div className="mt-3 animate-slide-down">
                  <label className="text-[10px] font-black text-red-500 uppercase tracking-wider">Manual Discount (₹)</label>
                  <input type="number" className={`${inputClass} !bg-white mt-1`} value={manualDiscount} onChange={(e) => setManualDiscount(e.target.value)} />
                </div>
              )}
            </div>

            <div className="p-5 bg-gray-900 rounded-2xl text-white shadow-xl shadow-gray-200">
              <div className="space-y-2 text-sm opacity-80">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-green-400"><span>Discount</span><span>- ₹{discountAmount.toFixed(2)}</span></div>
                {selectedPaymentMethod?.payment_method_id === 1 && (
                  <div className="flex justify-between"><span>COD Charges</span><span>₹{codCharge}</span></div>
                )}
              </div>
              <div className="flex justify-between font-bold text-2xl mt-4 pt-4 border-t border-gray-700 text-white">
                <span className="text-base font-medium opacity-70">Grand Total</span>
                <span>₹{finalTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-20 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">Your order is empty</p>
          <p className="text-sm text-gray-400">Scan a product barcode to get started.</p>
        </div>
      )}

      {/* --- 2. CUSTOMER SECTION --- */}
      <div className="mt-10 p-6 border border-gray-200 rounded-2xl bg-white shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Customer Details
          </h3>
          {addressLookupLoading && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full animate-pulse">Searching Database...</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mobile Number</label>
            <input type="text" maxLength="10" value={customerMobile} onChange={handleMobileChange} className={inputClass} placeholder="9876543210" disabled={isSubmitting || !!lastOrder} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name</label>
            <input type="text" value={customerData.customer_name} onChange={(e) => handleAddressChange('customer_name', e.target.value)} className={inputClass} placeholder="Required" disabled={isSubmitting || !!lastOrder || customerData.customer_id} />
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address Line 1</label>
            <input type="text" value={customerData.address_line_1} onChange={(e) => handleAddressChange('address_line_1', e.target.value)} className={inputClass} placeholder="Street, House No." disabled={isSubmitting || !!lastOrder} />
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address Line 2</label>
            <input type="text" value={customerData.address_line_2} onChange={(e) => handleAddressChange('address_line_2', e.target.value)} className={inputClass} placeholder="Area, Vill" disabled={isSubmitting || !!lastOrder} />
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address Line 2</label>
            <input type="text" value={customerData.land_mark} onChange={(e) => handleAddressChange('land_mark', e.target.value)} className={inputClass} placeholder="Land mark" disabled={isSubmitting || !!lastOrder} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">City</label>
            <input type="text" value={customerData.city} onChange={(e) => handleAddressChange('city', e.target.value)} className={inputClass} disabled={isSubmitting || !!lastOrder} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pincode</label>
            <input type="text" maxLength="6" value={customerData.pincode} onChange={(e) => handleAddressChange('pincode', e.target.value)} className={inputClass} disabled={isSubmitting || !!lastOrder} />
          </div>
          <div>
             <label className="block text-xs font-bold text-gray-500 uppercase mb-1">State</label>
             <select value={customerData.state_id} onChange={(e) => handleAddressChange('state_id', e.target.value)} className={inputClass} disabled={isSubmitting || !!lastOrder}>
                <option value="">Select State</option>
                {statesList.map(s => <option key={s.state_id} value={s.state_id}>{s.state_name}</option>)}
             </select>
          </div>
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-8">
        <button
          onClick={handleReset}
          className="w-full sm:w-auto px-6 py-3 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-100 transition-all"
          disabled={isSubmitting}
        >
          {lastOrder ? 'Start New Order' : 'Clear Form'}
        </button>

        {lastOrder ? (
          <button
            onClick={handlePrint}
            className="w-full sm:w-auto px-10 py-4 rounded-xl text-white font-bold bg-indigo-600 hover:bg-indigo-700 shadow-xl shadow-indigo-100 flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Label: {lastOrder.orderDetail.order_number}
          </button>
        ) : (
          <button
            onClick={handleCreateOrder}
            className={`w-full sm:w-auto px-12 py-4 rounded-xl text-white font-black text-lg transition-all shadow-xl ${
              orderItems.length === 0 || isSubmitting
                ? 'bg-emerald-300 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100'
            }`}
            disabled={orderItems.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Generating Order...' : 'Complete Order'}
          </button>
        )}
      </div>
    </div>
  </div>

  {/* GALLERY MODAL IMPROVEMENTS */}
  {selectedImages && (
    <div className="fixed inset-0 bg-gray-900/90 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
          <h3 className="font-bold text-gray-700">Product Visuals</h3>
          <button onClick={() => setSelectedImages(null)} className="h-8 w-8 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all">✕</button>
        </div>
        <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {selectedImages.map((img, index) => (
            <img key={index} src={img.main} className="w-full rounded-xl shadow-lg border border-gray-100" />
          ))}
        </div>
      </div>
    </div>
  )}

  {/* Hidden Print Component */}
  {lastOrder && (
    <div className="hidden">
      <ShippingLabel ref={labelRef} orderDetail={lastOrder.orderDetail} shippingDetail={lastOrder.shippingDetail} />
    </div>
  )}
</div>
    );
};

export default OfflineOrderEntry;